/**
 * Timeline Node
 *
 * @module features/releases/components/TimelineNode
 * @description The single 28px node rendered by both a commit row
 * (`TimelineCommitEntry`) and a release band (`TimelineRunEntry`). At rest it
 * shows either a pass-rate ring (when a `suiteRun` is given — the same
 * drawing `CommitSuiteGauge` used to own) or a plain status dot. On hover or
 * keyboard focus its contents cross-fade into a rocket, and pressing it
 * reports the commit upward to open the Ship dialog. When the node's commit
 * is the deployed sha it also carries a quiet "live" halo at rest, with no
 * hover required.
 *
 * The node is the ONLY ship control now — the old fixed-width right-hand
 * slot on `TimelineCommitEntry` (Deployed chip / hover Ship button) is gone.
 * The suite-execution link it used to own moved to the band's meta line
 * (`TimelineRunEntry`'s regression text).
 */

import React from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { RocketLaunch } from '@phosphor-icons/react';
import { Text, Tooltip, TooltipTrigger, getGaugeColor, useTheme } from '@chatsdet/ui';
import type { TimelineSuiteRun } from '../hooks/useReleaseTimeline';
import { getSuitePassRate } from './suiteRun';
import { shortSha as formatShortSha } from '../../runbooks/types';

/**
 * Ring geometry — fixed. `strokeWidth = 3`, `radius = 16 - strokeWidth/2` in a
 * `viewBox="0 0 32 32"` puts the ring's OUTER edge at the edge of the 28px
 * box, so a gauge node and a plain dot node share an outer edge (not just a
 * center). Do not change these numbers — see CommitSuiteGauge's original
 * comment; regressing them re-breaks the node column's alignment.
 */
const strokeWidth = 3;
const radius = 16 - strokeWidth / 2;
const circumference = 2 * Math.PI * radius;

const iconSwapTransition = { type: 'spring' as const, duration: 0.3, bounce: 0 };
const reducedMotionTransition = { duration: 0.15, ease: 'easeOut' as const };

export type TimelineNodeCommit = {
  sha: string;
  message: string;
};

type Props = {
  /** Renders the pass-rate ring when given; a plain dot otherwise. */
  suiteRun?: TimelineSuiteRun | null;
  /**
   * Dot colouring when no `suiteRun` is given. `tested`/`deployed` share the
   * same visual weight as the pre-existing commit/run dot palettes.
   */
  status?: 'neutral' | 'tested' | 'deployed' | 'success' | 'failed' | 'active';
  /** True when this node's commit is the currently-deployed sha. */
  isDeployed?: boolean;
  /**
   * The commit to ship, and the handler to ship it, when this node is
   * pressed. Both travel together structurally (rather than as two
   * independently-optional props) because the node only ever acts when both
   * are present — omit `ship` entirely to render a non-interactive node (no
   * button, no rocket hover, no tooltip), used by a band with no released
   * commit, which falls back to its own click-to-open-run behavior instead.
   */
  ship?: { commit: TimelineNodeCommit; onShip: (commit: TimelineNodeCommit) => void };
};

function dotClasses(status: Props['status'], isDeployed?: boolean) {
  if (isDeployed) return { ring: 'border-red-400 bg-red-50 cofactor-dark:border-red-700 cofactor-dark:bg-red-950', fill: 'bg-red-500' };
  switch (status) {
    case 'tested':
    case 'success':
      return { ring: 'border-green-300 bg-green-50 cofactor-dark:border-green-800 cofactor-dark:bg-green-950', fill: 'bg-green-500' };
    case 'failed':
      return { ring: 'border-red-300 bg-red-50 cofactor-dark:border-red-800 cofactor-dark:bg-red-950', fill: 'bg-red-500' };
    case 'active':
      return { ring: 'border-blue-300 bg-blue-50 cofactor-dark:border-blue-800 cofactor-dark:bg-blue-950 animate-pulse', fill: 'bg-blue-500' };
    default:
      return { ring: 'border-border bg-background', fill: 'bg-muted-foreground' };
  }
}

/**
 * The node splits into two layers on purpose.
 *
 * `useNodeLayers` returns a `ring` that is drawn unconditionally and never
 * animates, and a `center` that is what the rocket swaps with. The ring is the
 * node's identity on the rail — an earlier version animated the whole node out
 * on hover, so the rail appeared to lose a node wherever the pointer went.
 * Only the number (or the dot's fill) gives way to the rocket.
 */
function useNodeLayers({ suiteRun, status, isDeployed }: Pick<Props, 'suiteRun' | 'status' | 'isDeployed'>) {
  const { config } = useTheme();

  if (suiteRun) {
    const passRate = getSuitePassRate(suiteRun);
    const isActive = suiteRun.status === 'queued' || suiteRun.status === 'running';
    const score = passRate ?? 0;
    const strokeDashoffset = circumference - (score / 100) * circumference;
    const color =
      passRate === null
        ? isActive
          ? 'hsl(var(--primary))'
          : 'hsl(var(--muted-foreground))'
        : getGaugeColor(passRate, config.theme);
    const displayValue = passRate === null ? (isActive ? '…' : '—') : String(passRate);

    return {
      ring: (
        <svg aria-hidden="true" viewBox="0 0 32 32" className="h-8 w-8 -rotate-90">
          <circle
            cx="16"
            cy="16"
            r={radius}
            fill="hsl(var(--background))"
            stroke="hsl(var(--border))"
            strokeWidth={strokeWidth}
          />
          <circle
            cx="16"
            cy="16"
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={`${circumference} ${circumference}`}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-[stroke-dashoffset,stroke] duration-300 ease-out"
          />
        </svg>
      ),
      center: (
        <span className="text-[9px] font-semibold leading-none text-foreground tabular-nums">
          {displayValue}
        </span>
      ),
    };
  }

  const { ring, fill } = dotClasses(status, isDeployed);
  return {
    ring: <div className={`h-8 w-8 rounded-full border-2 ${ring}`} />,
    center: <div className={`h-2.5 w-2.5 rounded-full ${fill}`} />,
  };
}

/**
 * The word beside the pulse. Sits on its own line directly above the commit
 * sha/message it refers to (see the callers), aligned with that text column
 * rather than over the node — the node already carries its own live ring.
 *
 * The pulse itself lives on the node; this stays static so the row doesn't
 * carry two competing animations.
 */
export function LiveMarker() {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-red-600 cofactor-dark:text-red-400">
      <span className="block h-1.5 w-1.5 rounded-full bg-red-500" aria-hidden="true" />
      Live
    </span>
  );
}

export function TimelineNode({
  suiteRun = null,
  status = 'neutral',
  isDeployed = false,
  ship,
}: Props) {
  const [hovered, setHovered] = React.useState(false);
  const { ring, center } = useNodeLayers({ suiteRun, status, isDeployed });
  const prefersReducedMotion = useReducedMotion();
  const shippable = Boolean(ship);
  const showRocket = shippable && hovered;
  const transition = prefersReducedMotion ? reducedMotionTransition : iconSwapTransition;

  const swapIn = prefersReducedMotion
    ? { opacity: 1 }
    : { opacity: 1, scale: 1, filter: 'blur(0px)' };
  const swapInitial = prefersReducedMotion
    ? { opacity: 0 }
    : { opacity: 0, scale: 0.25, filter: 'blur(4px)' };
  const swapExit = swapInitial;

  const nodeContent = (
    <>
      {isDeployed && (
        // An outlined halo, not a filled disc: the node's own centre is
        // translucent, so a solid amber disc pinging out from under it washed
        // the whole node amber at the start of every cycle instead of reading
        // as a ring leaving it.
        <span
          aria-hidden="true"
          // Tailwind's `animate-ping` is 1s, which reads as an alarm on a
          // element that is always on screen. Slowed to a heartbeat.
          className="absolute inset-0 rounded-full border-2 border-red-500 opacity-60 animate-ping [animation-duration:2.6s] motion-reduce:animate-none"
        />
      )}
      {/* Drawn unconditionally — the ring never animates out, so the rail
          never appears to lose a node under the pointer. */}
      <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
        {ring}
      </span>
      <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <AnimatePresence mode="wait" initial={false}>
          {showRocket ? (
            <motion.span
              key="rocket"
              initial={swapInitial}
              animate={swapIn}
              exit={swapExit}
              transition={transition}
              className="flex items-center justify-center text-foreground"
            >
              <RocketLaunch size={14} weight="fill" />
            </motion.span>
          ) : (
            <motion.span
              key="resting"
              initial={swapInitial}
              animate={swapIn}
              exit={swapExit}
              transition={transition}
              className="flex items-center justify-center"
            >
              {center}
            </motion.span>
          )}
        </AnimatePresence>
      </span>
    </>
  );

  // A solid ring around the whole node — separate from the halo — is the
  // deployed node's at-rest "live" signal. Box-shadow based (Tailwind
  // `ring-*`), so it doesn't affect the node's 28px box or its center, which
  // the rail alignment depends on.
  const liveRingClasses = isDeployed ? 'ring-2 ring-red-500 ring-offset-2 ring-offset-background' : '';

  if (!ship) {
    return (
      <span
        data-timeline-node
        className={`relative z-10 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${liveRingClasses}`}
      >
        {nodeContent}
      </span>
    );
  }

  const { commit, onShip } = ship;
  const shortShaLabel = formatShortSha(commit.sha);
  const tooltipHint = isDeployed
    ? 'Live in production · click to ship a different build'
    : null;
  // Neutral: the dialog this opens offers several actions on this commit
  // (run a release, or promote to a stage), so "Ship <sha>" would
  // over-promise one of them.
  const ariaLabel = isDeployed
    ? `Release actions for ${shortShaLabel} · live in production, click to ship a different build`
    : `Release actions for ${shortShaLabel}`;

  return (
    // `top`, not `right`: the node sits at the far left of a full-width row,
    // so a right-placed tooltip lands squarely on the commit message it
    // belongs to.
    <TooltipTrigger placement="top" delay={100} offset={8}>
      <button
        type="button"
        data-timeline-node
        aria-label={ariaLabel}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        onClick={() => {
          // Reset immediately rather than relying on a `mouseleave` that a
          // touch tap never fires — otherwise the rocket sticks after a tap.
          // Gated to coarse-pointer (touch) devices only: on a desktop mouse,
          // the dialog this opens can close while the pointer is still over
          // the node, and no `mouseenter` refires in that case — resetting
          // unconditionally left the rocket hidden until the pointer left and
          // re-entered.
          if (typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches) {
            setHovered(false);
          }
          onShip(commit);
        }}
        className={`relative z-10 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full outline-none after:absolute after:-inset-1 after:rounded-full hover:scale-105 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 active:scale-[0.96] transition-transform ${liveRingClasses}`}
      >
        {nodeContent}
      </button>
      <Tooltip variant="light" className="px-3 py-1.5 text-left">
        <div className="flex items-baseline gap-1.5">
          <span className="text-xs font-medium text-foreground">Ship</span>
          <span className="font-mono text-xs text-muted-foreground">{shortShaLabel}</span>
        </div>
        {tooltipHint && (
          <Text size="xs" className="mt-0.5 text-muted-foreground">
            {tooltipHint}
          </Text>
        )}
      </Tooltip>
    </TooltipTrigger>
  );
}
